import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

import { computed, ref, resolveComponent } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import {
  ButtonVariantProps,
  ButtonSizeProps,
  ButtonVariantNewProps,
  ButtonSizeNewProps,
  ButtonSize,
  IconProps,
} from './types';
import RcIcon from '@components/RcIcon/RcIcon.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'RcButton',
  props: {
    primary: { type: Boolean },
    secondary: { type: Boolean },
    tertiary: { type: Boolean },
    link: { type: Boolean },
    multiAction: { type: Boolean },
    ghost: { type: Boolean },
    small: { type: Boolean },
    variant: {},
    size: { default: 'medium' },
    leftIcon: {},
    rightIcon: {},
    to: { default: undefined }
  },
  setup(__props: any, { expose: __expose }) {

/**
 * A button element used for performing actions, such as submitting forms or
 * opening dialogs.
 *
 * Example:
 *
 * <rc-button variant="primary" @click="doAction">Perform an Action</rc-button>
 */
const buttonVariants: { variant: keyof ButtonVariantProps, className: string }[] = [
  { variant: 'primary', className: 'variant-primary' },
  { variant: 'secondary', className: 'variant-secondary' },
  { variant: 'tertiary', className: 'variant-tertiary' },
  { variant: 'link', className: 'variant-link' },
  { variant: 'multiAction', className: 'variant-multi-action' },
  { variant: 'ghost', className: 'variant-ghost' },
];

const buttonSizes: { size: keyof ButtonSizeProps, className: string }[] = [
  { size: 'small', className: 'btn-sm' },
];

const buttonSizesNew: { size: ButtonSize, className: string }[] = [
  { size: 'small', className: 'btn-small' },
  { size: 'medium', className: 'btn-medium' },
  { size: 'large', className: 'btn-large' },
];

const props = __props;

const tag = computed(() => (props.to ? resolveComponent('RouterLink') : 'button'));
const role = computed(() => (props.to ? 'link' : 'button'));

const activeVariantClassName = computed(() => {
  if (props.variant === 'multiAction' || props.multiAction) {
    console.warn('[RcButton] The "multiAction" variant is deprecated and will be removed in a future version.'); // eslint-disable-line no-console
  }

  const activeVariant = buttonVariants.find(({ variant }) => props[variant]);

  if (activeVariant) {
    console.warn( // eslint-disable-line no-console
      `[RcButton] The "${ activeVariant.variant }" prop is deprecated and will be removed in a future version. ` +
      `Please use variant="${ activeVariant.variant }" instead.`
    );

    return activeVariant.className;
  } else {
    const variantConfig = buttonVariants.find(({ variant }) => variant === props.variant);

    return variantConfig?.className || 'variant-primary';
  }
});

const activeSizeClassName = computed(() => {
  const activeSize = buttonSizes.find(({ size }) => props[size]);

  if (activeSize) {
    /* eslint-disable no-console */
    console.warn(
      `[RcButton] The "${ activeSize.size }" prop is deprecated and will be removed in a future version. ` +
      `Please use size="${ activeSize.size }" instead.`
    );
    /* eslint-enable no-console */

    return activeSize.className;
  } else {
    const sizeConfig = buttonSizesNew.find(({ size }) => size === props.size);

    return sizeConfig?.className || '';
  }
});

const buttonClass = computed(() => {
  return {
    'rc-button':                    true,
    btn:                            true,
    [activeVariantClassName.value]: true,
    [activeSizeClassName.value]:    !!activeSizeClassName.value,
  };
});

const RcFocusTarget = ref<HTMLElement | null>(null);

const focus = () => {
  RcFocusTarget?.value?.focus();
};

__expose({ focus });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_resolveDynamicComponent(tag.value), {
    ref_key: "RcFocusTarget",
    ref: RcFocusTarget,
    role: role.value,
    to: __props.to,
    class: _normalizeClass({ ...buttonClass.value })
  }, {
    default: _withCtx(() => [
      (_ctx.$slots.before || props.leftIcon)
        ? _renderSlot(_ctx.$slots, "before", { key: 0 }, () => [
            (props.leftIcon)
              ? (_openBlock(), _createBlock(RcIcon, {
                  key: 0,
                  class: "left-icon",
                  type: props.leftIcon,
                  size: "inherit"
                }, null, 8, ["type"]))
              : _createCommentVNode("", true)
          ])
        : _createCommentVNode("", true),
      _cache[0] || (_cache[0] = _createTextVNode()),
      _renderSlot(_ctx.$slots, "default"),
      _cache[1] || (_cache[1] = _createTextVNode()),
      (_ctx.$slots.after || props.rightIcon)
        ? _renderSlot(_ctx.$slots, "after", { key: 1 }, () => [
            (props.rightIcon)
              ? (_openBlock(), _createBlock(RcIcon, {
                  key: 0,
                  class: "right-icon",
                  type: props.rightIcon,
                  size: "inherit"
                }, null, 8, ["type"]))
              : _createCommentVNode("", true)
          ])
        : _createCommentVNode("", true)
    ]),
    _: 3
  }, 8, ["role", "to", "class"]))
}
}

})